import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { turnosApi } from '@/api/turnos'
import type { Observacion, Turno, Venta } from '@/types/api'

export const useTurnoStore = defineStore('turno', () => {
  const turnoActual = ref<Turno | null>(null)
  const cargando = ref(false)
  const error = ref<string | null>(null)

  const tieneAbierto = computed(() => turnoActual.value !== null && !turnoActual.value.cerrado)

  async function cargar() {
    cargando.value = true
    error.value = null
    try {
      turnoActual.value = await turnosApi.actual()
    } catch (e: unknown) {
      error.value = (e as Error).message
    } finally {
      cargando.value = false
    }
  }

  async function abrir(notas?: string) {
    turnoActual.value = await turnosApi.abrir(notas ?? null)
  }

  async function cerrar(notas?: string) {
    const cerrado = await turnosApi.cerrar(notas ?? null)
    turnoActual.value = null
    return cerrado
  }

  async function agregarObservacion(descripcion: string, monto: number) {
    const obs = await turnosApi.agregarObservacion(descripcion, monto)
    if (turnoActual.value) {
      turnoActual.value.observaciones = [...turnoActual.value.observaciones, obs]
    }
    return obs
  }

  function aplicarTurnoEvent(turno: Turno) {
    turnoActual.value = turno.cerrado ? null : turno
  }

  function aplicarObservacionEvent(obs: Observacion) {
    if (!turnoActual.value) return
    const ya = turnoActual.value.observaciones.find((o) => o.id === obs.id)
    if (!ya) {
      turnoActual.value.observaciones = [...turnoActual.value.observaciones, obs]
    }
  }

  /**
   * Suma una venta a los totales en vivo del turno abierto. Debe llamarse
   * exactamente una vez por venta (lo garantiza el dedupe por id en el store
   * de ventas). Mantiene el conteo/monto en sincronía con lo que devolvería
   * un refetch de `/turnos/actual`, sin esperar a recargar.
   */
  function contarVenta(venta: Venta) {
    const t = turnoActual.value
    if (!t || t.cerrado || venta.turno_id !== t.id) return
    t.cantidad_ventas += 1
    t.total_ventas += venta.total
    if (venta.metodo_pago === 'efectivo') t.total_efectivo += venta.total
    else if (venta.metodo_pago === 'tarjeta') t.total_tarjeta += venta.total
    else if (venta.metodo_pago === 'transferencia') t.total_transferencia += venta.total
  }

  /** Inversa de `contarVenta`: se llama una sola vez por anulación. */
  function descontarVenta(venta: Venta) {
    const t = turnoActual.value
    if (!t || t.cerrado || venta.turno_id !== t.id) return
    t.cantidad_ventas = Math.max(0, t.cantidad_ventas - 1)
    t.total_ventas = Math.max(0, t.total_ventas - venta.total)
    if (venta.metodo_pago === 'efectivo') t.total_efectivo = Math.max(0, t.total_efectivo - venta.total)
    else if (venta.metodo_pago === 'tarjeta') t.total_tarjeta = Math.max(0, t.total_tarjeta - venta.total)
    else if (venta.metodo_pago === 'transferencia') {
      t.total_transferencia = Math.max(0, t.total_transferencia - venta.total)
    }
  }

  return {
    turnoActual,
    cargando,
    error,
    tieneAbierto,
    cargar,
    abrir,
    cerrar,
    agregarObservacion,
    aplicarTurnoEvent,
    aplicarObservacionEvent,
    contarVenta,
    descontarVenta,
  }
})
